import React, {useState} from 'react';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {configApiRef, useApi} from '@backstage/core-plugin-api';
import {utilitiesApiRef} from '../../../../scaffolder/utils/bitbucketUtilitiesApi';
import {
  COMPONENT_TYPES,
  LIFECYCLE_OPTIONS,
  SYSTEM_OPTIONS,
  RegisterWizardFormValues,
} from '../types';
import {slugFromRepoUrl} from '../generateCatalogInfo';
import {LoggedInUser} from '../useLoggedInUser';

const BITBUCKET_WORKSPACE_PREFIX = 'https://bitbucket.org/parivedasolutions/';

export type DetailsCompletion = {
  hasPermission: boolean;
  mainBranch: string;
};

type DetailsFormStepProps = {
  initialValues: RegisterWizardFormValues;
  loggedInUser?: LoggedInUser;
  onNext: (values: RegisterWizardFormValues, completion: DetailsCompletion) => void;
};

export const DetailsFormStep = ({
  initialValues,
  loggedInUser,
  onNext,
}: DetailsFormStepProps) => {
  const configApi = useApi(configApiRef);
  const utilitiesApi = useApi(utilitiesApiRef);
  const [values, setValues] = useState(initialValues);
  const [repoError, setRepoError] = useState<string>();
  const [checkingRepo, setCheckingRepo] = useState(false);
  const [permissionError, setPermissionError] = useState<string>();
  const [requestingAccess, setRequestingAccess] = useState(false);
  const [accessRequested, setAccessRequested] = useState(false);

  const setField = <K extends keyof RegisterWizardFormValues>(
    field: K,
    value: RegisterWizardFormValues[K],
  ) => {
    setValues(prev => ({...prev, [field]: value}));
  };

  // Checks (and records) whether the logged-in user has permission to open a
  // PR on this repo, but never blocks progress on it -- callers without
  // permission still get to continue, just down the manual-push path on the
  // next step instead of the automatic pull-request path.
  const checkPermission = async (slug: string): Promise<boolean> => {
    if (!loggedInUser?.email) {
      return true;
    }
    const hasPermission = await utilitiesApi.doesUserHavePermission(slug, loggedInUser.email);
    setPermissionError(
      hasPermission
        ? undefined
        : `You don't appear to have access to open a pull request on "${slug}". You can still continue -- the next step will walk you through adding catalog-info.yaml by hand. Request access below, or ask a repo admin to add you, to unlock automatic pull requests.`,
    );
    return hasPermission;
  };

  const validateRepoUrl = async (): Promise<
    {slug: string; mainBranch: string; hasPermission: boolean} | undefined
  > => {
    setPermissionError(undefined);
    setAccessRequested(false);
    if (!values.repoUrl.startsWith(BITBUCKET_WORKSPACE_PREFIX)) {
      setRepoError(
        'URL must be under the Pariveda Solutions Bitbucket workspace (https://bitbucket.org/parivedasolutions/...)',
      );
      return undefined;
    }
    const slug = slugFromRepoUrl(values.repoUrl);
    if (!slug) {
      setRepoError('Could not determine a repository name from this URL');
      return undefined;
    }
    setCheckingRepo(true);
    try {
      const {exists, mainBranch} = await utilitiesApi.repoExists(
        slug,
        configApi.get('backend.baseUrl'),
      );
      if (!exists) {
        setRepoError(`No repository named "${slug}" was found in the Pariveda Solutions workspace`);
        return undefined;
      }
      if (!mainBranch) {
        setRepoError(`Could not determine the default branch for "${slug}"`);
        return undefined;
      }
      setRepoError(undefined);
      const hasPermission = await checkPermission(slug);
      return {slug, mainBranch, hasPermission};
    } catch (e) {
      setRepoError((e as Error).message);
      return undefined;
    } finally {
      setCheckingRepo(false);
    }
  };

  const handleRequestAccess = async () => {
    const slug = slugFromRepoUrl(values.repoUrl);
    if (!slug || !loggedInUser?.email) {
      return;
    }
    setRequestingAccess(true);
    try {
      const success = await utilitiesApi.inviteUser(slug, loggedInUser.email);
      setAccessRequested(success);
      if (!success) {
        setPermissionError(
          'Could not request access automatically. Please ask a repo admin to add you.',
        );
      }
    } finally {
      setRequestingAccess(false);
    }
  };

  const isComplete = Boolean(
    values.repoUrl && values.description && values.type && values.lifecycle && values.system,
  );

  const handleNext = async () => {
    const repoInfo = await validateRepoUrl();
    if (!repoInfo) {
      return;
    }
    onNext(values, {hasPermission: repoInfo.hasPermission, mainBranch: repoInfo.mainBranch});
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          label='Repository URL'
          placeholder='https://bitbucket.org/parivedasolutions/my-repo'
          value={values.repoUrl}
          onChange={e => setField('repoUrl', e.target.value)}
          error={!!repoError}
          helperText={repoError ?? 'Must be under the Pariveda Solutions Bitbucket workspace'}
        />
      </Grid>
      {permissionError && (
        <Grid item xs={12}>
          <Typography variant='body2' color='error' paragraph>
            {permissionError}
          </Typography>
          {accessRequested ? (
            <Typography variant='body2' color='textSecondary'>
              Access requested — try again once you've been granted permission.
            </Typography>
          ) : (
            <Button variant='outlined' onClick={handleRequestAccess} disabled={requestingAccess}>
              {requestingAccess ? 'Requesting…' : 'Request Access'}
            </Button>
          )}
        </Grid>
      )}
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          multiline
          minRows={2}
          label='Description'
          value={values.description}
          onChange={e => setField('description', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label='Website'
          placeholder='https://... (optional)'
          value={values.website}
          onChange={e => setField('website', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label='Slack Channel ID'
          placeholder='C0123456789 (optional)'
          value={values.slackChannelId}
          onChange={e => setField('slackChannelId', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth required>
          <InputLabel>Type</InputLabel>
          <Select
            value={values.type}
            onChange={e => setField('type', e.target.value as RegisterWizardFormValues['type'])}
          >
            {COMPONENT_TYPES.map(type => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth required>
          <InputLabel>Lifecycle</InputLabel>
          <Select
            value={values.lifecycle}
            onChange={e =>
              setField('lifecycle', e.target.value as RegisterWizardFormValues['lifecycle'])
            }
          >
            {LIFECYCLE_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth required>
          <InputLabel>System</InputLabel>
          <Select
            value={values.system}
            onChange={e => setField('system', e.target.value as RegisterWizardFormValues['system'])}
          >
            {SYSTEM_OPTIONS.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label='Tags'
          placeholder='react, aws, utility'
          helperText='Comma-separated (optional)'
          value={values.tags}
          onChange={e => setField('tags', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant='body2' color='textSecondary'>
          {loggedInUser
            ? `Registering as ${loggedInUser.email || loggedInUser.userEntityRef}. spec.owner will remain "parivedasolutions".`
            : 'Loading your account…'}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Button
          variant='contained'
          color='primary'
          disabled={!isComplete || checkingRepo || !loggedInUser}
          onClick={handleNext}
        >
          {checkingRepo ? 'Checking repository…' : 'Next'}
        </Button>
      </Grid>
    </Grid>
  );
};
